import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function InventoryDashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboard()
  }, [])

  const fetchDashboard = () => {
    setLoading(true)
    fetch('/api/inventory/dashboard')
      .then(response => response.json())
      .then(data => {
        setData(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching inventory dashboard:', err)
        setLoading(false)
      })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading inventory...</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Failed to load inventory data</div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      {/* Header with totals */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventory Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">
            {data.total_parts_count} total parts, {data.reorder_count} need reorder
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-6 text-right">
          <div>
            <p className="text-sm text-gray-500">In Stock</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(data.total_in_stock_value)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Need to Order</p>
            <p className="text-xl font-bold text-red-600">{formatCurrency(data.total_to_order_value)}</p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="mb-6 flex space-x-4">
        <Link
          to="/completebuild"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
        >
          Complete a Build
        </Link>
        <Link
          to="/parts/new"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          + Add Part
        </Link>
        <Link
          to="/builds"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          View Build History
        </Link>
        <Link
          to="/update_inventory"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Update Inventory
        </Link>
      </div>

      {/* Needs Reorder Section */}
      {data.parts_needing_reorder.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-red-800 mb-4">
            Needs Reorder ({data.parts_needing_reorder.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-red-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Part</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">In Stock</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Threshold</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Need to Order</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Vendor</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100">
                {data.parts_needing_reorder.map((part) => (
                  <tr key={part.id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link to={`/parts/${part.id}`} className="text-red-900 hover:underline font-medium">
                        {part.description}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-800">
                      {part.quantity_in_stock}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-700">
                      {part.reorder_threshold}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-red-800">
                      {part.quantity_to_order}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-700 max-w-xs break-words">
                      {part.vendor_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {part.vendor_website ? (
                        <a
                          href={part.vendor_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                        >
                          Order &rarr;
                        </a>
                      ) : (
                        <span className="text-red-400 text-sm">No link</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* In Stock Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            In Stock ({data.parts_in_stock.length})
          </h2>
          <Link to="/parts" className="text-sm text-gray-600 hover:text-gray-900">
            View All Parts &rarr;
          </Link>
        </div>

        {data.parts_in_stock.length === 0 ? (
          <p className="text-gray-500">No parts in stock</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {data.parts_in_stock.map((part) => (
              <Link
                key={part.id}
                to={`/parts/${part.id}`}
                className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors"
              >
                {part.photo_thumbnail_url ? (
                  <img
                    src={part.photo_thumbnail_url}
                    alt={part.description}
                    className="w-full h-24 object-cover rounded mb-2"
                  />
                ) : (
                  <div className="w-full h-24 bg-gray-200 rounded mb-2 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No image</span>
                  </div>
                )}
                <p className="text-sm font-medium text-gray-900 truncate">{part.description}</p>
                <p className="text-xs text-gray-500">Qty: {part.quantity_in_stock}</p>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Management Links */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/parts"
          className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <h3 className="font-medium text-gray-900">Manage Parts</h3>
          <p className="text-sm text-gray-500">Add, edit, or delete parts</p>
        </Link>
        <Link
          to="/configurations"
          className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <h3 className="font-medium text-gray-900">Manage Configurations</h3>
          <p className="text-sm text-gray-500">Define base build templates</p>
        </Link>
        <Link
          to="/options"
          className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <h3 className="font-medium text-gray-900">Manage Options</h3>
          <p className="text-sm text-gray-500">Define add-on packages</p>
        </Link>
      </div>
    </div>
  )
}
